import { useEffect, useState } from 'preact/hooks';
import { api, type Dataset } from '@/lib/teaching/teacher-api';
import { useAssessmentId } from '@/lib/teaching/state';
import { errText, type DecisionBody, type Guide as GuideT, type GuideItem } from '@/lib/teaching/slice2';
import { Card } from '@/components/teaching/ui';
import { Btn, ErrorLine, Field, val } from '@/components/teaching/form';
import { SetupPage, Note, textareaCls, selectCls } from '@/components/teaching/setup';

// Marking guide (§05): one versioned snapshot per save. Once marking has
// started, every changed question needs a moderation decision (S19, S31).

const emptyItem = (id: string, label: string): GuideItem => ({
  item_id: id, item_label: label, expected_response: '', alternatives: '', partial_credit_rules: '', required_working: '', wrong_interpretations: [],
});
const norm = (g: GuideItem | undefined) => JSON.stringify(g ? [g.expected_response, g.alternatives, g.partial_credit_rules, g.required_working,
  g.wrong_interpretations.map((w) => ({ text: w.text, code_ids: [...w.code_ids].sort() }))] : ['', '', '', '', []]);

const FIELDS: { key: 'expected_response' | 'alternatives' | 'partial_credit_rules' | 'required_working'; label: string }[] = [
  { key: 'expected_response', label: 'Expected response' },
  { key: 'alternatives', label: 'Acceptable alternatives' },
  { key: 'partial_credit_rules', label: 'Partial credit' },
  { key: 'required_working', label: 'Required working' },
];

interface DecisionDraft { original_interpretation: string; teacher_decision: string; retro: boolean; notes: string }
const blankDecision = (): DecisionDraft => ({ original_interpretation: '', teacher_decision: '', retro: true, notes: '' });

export function Guide() {
  const a = useAssessmentId();
  const [g, setG] = useState<GuideT | null>(null);
  const [ds, setDs] = useState<Dataset | null>(null);
  const [items, setItems] = useState<GuideItem[]>([]);
  const [school, setSchool] = useState('');
  const [covers, setCovers] = useState(false);
  const [viewing, setViewing] = useState<GuideT | null>(null);
  const [decisions, setDecisions] = useState<Record<string, DecisionDraft>>({});
  const [wide, setWide] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    if (!a) return;
    const [gg, d] = await Promise.all([api<GuideT>('GET', `/api/t/assessments/${a}/guide`), api<Dataset>('GET', `/api/t/assessments/${a}/dataset`)]);
    setG(gg); setDs(d);
    setItems(d.items.map((it) => gg.items.find((x) => x.item_id === it.id) ?? emptyItem(it.id, it.label)));
    setSchool(gg.school_guide_text ?? '');
    setCovers(!!gg.covers_all);
    setDecisions({}); setWide(false); setViewing(null);
  }
  useEffect(() => { setG(null); void load().catch((e) => setErr(errText(e))); }, [a]);

  const cur = new Map((g?.items ?? []).map((x) => [x.item_id, x]));
  const changed = items.filter((x) => norm(cur.get(x.item_id)) !== norm(x));
  const schoolChanged = (g?.school_guide_text ?? '') !== school;
  const coversChanged = !!g?.covers_all !== covers;
  const needDecisions = !!g?.marking_started && (changed.length > 0 || schoolChanged);
  const mustWide = needDecisions && schoolChanged;
  const useWide = wide || mustWide;
  const dirty = changed.length > 0 || schoolChanged || coversChanged;

  const set = (id: string, patch: Partial<GuideItem>) => setItems(items.map((x) => (x.item_id === id ? { ...x, ...patch } : x)));
  const dec = (k: string) => decisions[k] ?? blankDecision();
  const setDec = (k: string, patch: Partial<DecisionDraft>) => setDecisions({ ...decisions, [k]: { ...dec(k), ...patch } });

  async function save() {
    if (!a || !g) return;
    setErr(null); setFields({}); setMsg(null); setSaving(true);
    const toBody = (item_id: string | null, d: DecisionDraft): DecisionBody => ({
      item_id, scope: 'assessment', retro: d.retro, original_interpretation: d.original_interpretation.trim(), teacher_decision: d.teacher_decision.trim(), notes: d.notes.trim(),
    });
    const decs = !needDecisions ? [] : useWide ? [toBody(null, dec('*'))] : changed.map((x) => toBody(x.item_id, dec(x.item_id)));
    try {
      await api('POST', `/api/t/assessments/${a}/guide`, { expectedVersion: g.current_version, school_guide_text: school.trim() || null, covers_all: covers && !!school.trim(), items, ...(decs.length ? { decisions: decs } : {}) });
      await load();
      setMsg(decs.length ? `Saved. ${decs.length} moderation decision${decs.length === 1 ? '' : 's'} added to the log.` : 'Saved a new guide version.');
    } catch (e) { setErr(errText(e)); setFields((e as { fields?: Record<string, string> }).fields ?? {}); }
    setSaving(false);
  }

  async function view(v: number) {
    if (!a || !g) return;
    if (v === g.current_version) { setViewing(null); return; }
    try { setViewing(await api<GuideT>('GET', `/api/t/assessments/${a}/guide?version=${v}`)); } catch (e) { setErr(errText(e)); }
  }

  const linked = (itemId: string) => ds?.misconceptions.filter((m) => m.item_ids.includes(itemId)) ?? [];
  return (
    <SetupPage a={a} title="Marking guide">
      {!g && !err && <div class="text-[13px] text-[var(--color-text-muted)]">Loading…</div>}
      {g && ds && (
        <div class="grid gap-4 lg:grid-cols-[minmax(0,1fr)_260px] items-start">
          <div class="flex flex-col gap-4 min-w-0">
            {g.marking_started && <Note tone="warn">Marking has started. Saving a change records a moderation decision for each changed question, and AI marks suggested under the old guide are set aside until those papers are re-marked.</Note>}
            {viewing && (
              <Card title={`Version ${viewing.version} (read only)`} right={<Btn onClick={() => setViewing(null)}>Back to current</Btn>}>
                <GuideReadOnly g={viewing} />
              </Card>
            )}
            {!viewing && (
              <>
                <Card title="School marking guide" sub="Optional: paste the school's guide; it is sent with every marking request.">
                  <textarea aria-label="School guide text" rows={4} class={textareaCls} value={school} onInput={(e) => setSchool(val(e))} />
                  {fields.school_guide_text && <ErrorLine error={fields.school_guide_text} />}
                  <label class="mt-2 inline-flex items-center gap-1.5 text-[12.5px] text-[var(--color-text)]">
                    <input type="checkbox" checked={covers} disabled={!school.trim()} onChange={() => setCovers(!covers)} />
                    School guide covers all items
                  </label>
                  <div class="text-[11.5px] text-[var(--color-text-faint)]">Tick only if the pasted guide has an expected answer for every question. Otherwise fill each question below; checkpoint 1 checks this.</div>
                  {fields.covers_all && <ErrorLine error={fields.covers_all} />}
                </Card>
                {ds.items.length === 0 && <Card><div class="text-[13px] text-[var(--color-text-muted)]">This test has no questions yet.</div></Card>}
                {items.map((x) => {
                  const it = ds.items.find((i) => i.id === x.item_id)!;
                  const codes = linked(x.item_id);
                  const isChanged = changed.includes(x);
                  return (
                    <Card key={x.item_id} title={`${x.item_label} · ${it.max_mark} mark${it.max_mark === 1 ? '' : 's'}`} sub={it.prompt} right={isChanged ? <span class="text-[11px] font-semibold text-[#b45309]">changed</span> : undefined}>
                      <div class="grid gap-3 md:grid-cols-2">
                        {FIELDS.map((f) => (
                          <Field key={f.key} label={f.label}>
                            <textarea aria-label={`${f.label} ${x.item_label}`} rows={2} class={textareaCls} value={x[f.key]} onInput={(e) => set(x.item_id, { [f.key]: val(e) })} />
                          </Field>
                        ))}
                      </div>
                      <div class="mt-3 flex flex-col gap-2">
                        <div class="text-[12px] text-[var(--color-text-muted)]">Wrong interpretations</div>
                        {x.wrong_interpretations.map((w, j) => (
                          <div key={j} class="rounded-lg border border-[var(--color-border)] p-2 flex flex-col gap-1.5">
                            <div class="flex gap-2">
                              <input aria-label={`Wrong interpretation ${x.item_label} ${j + 1}`} class={textareaCls + ' h-9'} value={w.text}
                                onInput={(e) => set(x.item_id, { wrong_interpretations: x.wrong_interpretations.map((y, k) => (k === j ? { ...y, text: val(e) } : y)) })} />
                              <Btn kind="danger" onClick={() => set(x.item_id, { wrong_interpretations: x.wrong_interpretations.filter((_, k) => k !== j) })}>Remove</Btn>
                            </div>
                            <div class="flex flex-wrap gap-1.5">
                              {codes.length === 0 && <span class="text-[11.5px] text-[var(--color-text-faint)]">No misconception codes linked to {x.item_label}.</span>}
                              {codes.map((m) => {
                                const on = w.code_ids.includes(m.id);
                                return (
                                  <label key={m.id} class="text-[12px] px-2 py-0.5 rounded-lg border border-[var(--color-border)] cursor-pointer text-[var(--color-text)]" title={m.meaning}>
                                    <input type="checkbox" class="mr-1" checked={on} onChange={() => set(x.item_id, {
                                      wrong_interpretations: x.wrong_interpretations.map((y, k) => (k === j ? { ...y, code_ids: on ? y.code_ids.filter((c) => c !== m.id) : [...y.code_ids, m.id] } : y)),
                                    })} />{m.code}
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                        <div><Btn onClick={() => set(x.item_id, { wrong_interpretations: [...x.wrong_interpretations, { text: '', code_ids: [] }] })}>Add wrong interpretation</Btn></div>
                      </div>
                      {fields[`items.${x.item_id}`] && <ErrorLine error={fields[`items.${x.item_id}`]} />}
                    </Card>
                  );
                })}

                {needDecisions && (
                  <Card title="Moderation decisions" sub="Each change after marking has started is a recorded decision (SOP §14).">
                    {!mustWide && (
                      <label class="text-[12.5px] text-[var(--color-text)] flex items-center gap-1.5 mb-3">
                        <input type="checkbox" checked={wide} onChange={() => setWide(!wide)} />One whole-test decision covers every change
                      </label>
                    )}
                    {mustWide && <div class="mb-3 text-[12px] text-[var(--color-text-muted)]">The school guide changed, so this needs one whole-test decision.</div>}
                    <div class="flex flex-col gap-3">
                      {(useWide ? [{ k: '*', label: 'Whole test' }] : changed.map((x) => ({ k: x.item_id, label: x.item_label }))).map(({ k, label }) => (
                        <DecisionForm key={k} label={label} d={dec(k)} onChange={(p) => setDec(k, p)} />
                      ))}
                    </div>
                  </Card>
                )}

                <div class="flex flex-wrap items-center gap-2">
                  <Btn kind="primary" disabled={!dirty || saving} onClick={save}>Save guide (version {g.current_version + 1})</Btn>
                  {msg && <span class="text-[12.5px] text-[#15803d]">{msg}</span>}
                </div>
                <ErrorLine error={err} />
              </>
            )}
          </div>
          <Card title="Version history">
            {g.versions.length === 0 && <div class="text-[12.5px] text-[var(--color-text-muted)]">No versions saved yet.</div>}
            <ul class="flex flex-col gap-1">
              {g.versions.map((v) => (
                <li key={v.version}>
                  <button type="button" onClick={() => view(v.version)} class={'w-full text-left rounded-lg px-2 py-1.5 text-[12.5px] ' + ((viewing?.version ?? g.current_version) === v.version ? 'bg-[var(--color-accent-soft)]' : '')}>
                    <b class="text-[var(--color-text)]">v{v.version}</b>{v.version === g.current_version && <span class="text-[var(--color-text-muted)]"> (current)</span>}
                    <div class="text-[11px] text-[var(--color-text-faint)]">{new Date(v.created_at).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })} · {v.created_by}</div>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}
      {err && !g && <ErrorLine error={err} />}
    </SetupPage>
  );
}

export function DecisionForm({ label, d, onChange }: { label: string; d: DecisionDraft; onChange: (p: Partial<DecisionDraft>) => void }) {
  return (
    <div class="rounded-xl border border-[var(--color-border)] p-3 flex flex-col gap-2">
      <div class="text-[12.5px] font-semibold text-[var(--color-text)]">{label}</div>
      <div class="grid gap-2 md:grid-cols-2">
        <Field label="Original interpretation"><textarea aria-label={`Original interpretation ${label}`} rows={2} class={textareaCls} value={d.original_interpretation} onInput={(e) => onChange({ original_interpretation: val(e) })} /></Field>
        <Field label="Teacher decision"><textarea aria-label={`Teacher decision ${label}`} rows={2} class={textareaCls} value={d.teacher_decision} onInput={(e) => onChange({ teacher_decision: val(e) })} /></Field>
      </div>
      <div class="flex flex-wrap gap-3 items-center">
        <label class="text-[12.5px] text-[var(--color-text)] flex items-center gap-1.5"><input type="checkbox" checked={d.retro} onChange={() => onChange({ retro: !d.retro })} />Recheck earlier papers</label>
        <input aria-label={`Notes ${label}`} class={selectCls + ' flex-1 min-w-[160px]'} placeholder="Notes (optional)" value={d.notes} onInput={(e) => onChange({ notes: val(e) })} />
      </div>
    </div>
  );
}

function GuideReadOnly({ g }: { g: GuideT }) {
  return (
    <div class="flex flex-col gap-3 text-[12.5px]">
      {g.school_guide_text && <div><div class="text-[var(--color-text-muted)]">School guide</div><div class="whitespace-pre-wrap text-[var(--color-text)]">{g.school_guide_text}</div></div>}
      {g.items.map((x) => (
        <div key={x.item_id} class="border-t border-[var(--color-border)] pt-2">
          <b class="text-[var(--color-text)]">{x.item_label}</b>
          {FIELDS.map((f) => x[f.key] ? <div key={f.key}><span class="text-[var(--color-text-muted)]">{f.label}: </span><span class="text-[var(--color-text)]">{x[f.key]}</span></div> : null)}
          {x.wrong_interpretations.map((w, i) => <div key={i} class="text-[var(--color-text)]">Wrong: {w.text}</div>)}
        </div>
      ))}
    </div>
  );
}
